import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Package, Sparkles } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SuccessPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { order, selectedStyle } = location.state || {};
  const [qikinkOrderId, setQikinkOrderId] = useState(null);

  // Poll for Qikink order ID (it's set asynchronously by process_order background task)
  useEffect(() => {
    if (!order?.order_id) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await axios.get(`${API}/orders/${order.order_id}`);
        const qid = res.data?.order?.qikink_order_id;
        if (qid && !cancelled) {
          setQikinkOrderId(qid);
          return; // stop polling
        }
      } catch (_) {/* ignore */}
      if (!cancelled) setTimeout(tick, 3000);
    };
    tick();
    return () => { cancelled = true; };
  }, [order?.order_id]);

  if (!order) {
    navigate("/");
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="glass-header py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-2xl font-black tracking-tight text-center">
            theSnugglePrints
          </h1>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Success Icon */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-primary/10 mb-6">
              <CheckCircle2 className="w-12 h-12 text-primary" />
            </div>
            <h2 className="text-4xl sm:text-5xl tracking-tighter font-black mb-4">
              Order Confirmed!
            </h2>
            <p className="text-base leading-relaxed font-medium text-muted-foreground">
              Thank you for your order. Your custom pet portrait will be
              printed, framed, and shipped to your door.
              <br />
              <span className="text-sm">
                Your order will be dispatched within 1 business day and will reach you within 3-5 business days after shipment.
              </span>
            </p>
          </div>

          {/* Order Details */}
          <div className="bg-card border border-border/60 p-8 sm:p-12 mb-8">
            <div className="flex items-start gap-6 mb-6 pb-6 border-b border-border">
              {selectedStyle && (
                <div className="w-32 h-32 flex-shrink-0 overflow-hidden">
                  <img
                    src={selectedStyle.preview_url || selectedStyle.image_url}
                    alt="Your pet portrait"
                    className="w-full h-full object-contain bg-white"
                    data-testid="success-style-image"
                  />
                </div>
              )}
              <div className="flex-1">
                <Label className="label-uppercase mb-2 block">
                  Order Reference
                </Label>
                <p
                  className="text-2xl font-black tracking-tight mb-4"
                  data-testid="order-reference-number"
                >
                  {order.reference_number}
                </p>
                {qikinkOrderId && (
                  <>
                    <Label className="label-uppercase mb-1 block">
                      Fulfillment ID
                    </Label>
                    <p
                      className="text-lg font-bold mb-4"
                      data-testid="qikink-order-id"
                    >
                      #{qikinkOrderId}
                    </p>
                  </>
                )}
                <Label className="label-uppercase mb-1 block">
                  Product
                </Label>
                <p className="text-lg font-bold">
                  Custom Pet Portrait, framed A4 artboard
                </p>
              </div>
            </div>

            {/* Status Timeline */}
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-primary flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-bold">Order Placed</p>
                  <p className="text-sm text-muted-foreground">
                    Your order has been received
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-8 h-8 border-2 border-primary flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-bold">Design Processing</p>
                  <p className="text-sm text-muted-foreground">
                    We're preparing your custom design for printing
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-8 h-8 border-2 border-border flex items-center justify-center flex-shrink-0">
                  <Package className="w-5 h-5 text-muted-foreground" />
                </div>
                <div>
                  <p className="font-bold text-muted-foreground">Production & Shipping</p>
                  <p className="text-sm text-muted-foreground">
                    You'll receive tracking details via email
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Next Steps */}
          <div className="bg-muted/30 border border-border/60 p-8">
            <h3 className="text-xl font-bold mb-4">What happens next?</h3>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-2">
                <span className="text-primary font-bold">•</span>
                <span>
                  You'll receive an order confirmation email with your order
                  details
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary font-bold">•</span>
                <span>
                  Your custom design will be printed and shipped within a day
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary font-bold">•</span>
                <span>
                  Once shipped, your order will reach you within 3-5 business
                  days
                </span>
              </li>
            </ul>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4 mt-8">
            <Button
              onClick={() => navigate("/")}
              variant="outline"
              className="flex-1 h-12"
              data-testid="create-another-button"
            >
              Create Another Design
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

const Label = ({ children, className = "" }) => {
  return <div className={className}>{children}</div>;
};

export default SuccessPage;
